/**
 * Auswertung der Nutzeraktivität — reine Funktionen, keine Seiteneffekte.
 *
 * Hier wird ausschliesslich gerechnet: aus dem rohen Ereignisstrom
 * (`user_activity_events`) entstehen Projektsitzungen, Tageskennzahlen und
 * Tagesrouten. Kein React, kein Supabase, kein DOM — damit die Regeln ohne
 * jsdom testbar bleiben (im Repo gibt es bewusst keine DOM-Testumgebung).
 *
 * Drei Regeln stehen über allem:
 *
 * 1. Eine Dauer wird NIE geschätzt. Fehlt das Schliessen-Ereignis, bleibt
 *    `durationMs` null — nicht "bis jetzt", nicht "bis Tagesende", nicht
 *    gekappt. Eine erfundene Dauer wäre in der Auswertung nicht mehr von einer
 *    gemessenen zu unterscheiden.
 * 2. Die Tageszuordnung folgt der LOKALEN Zeit des Browsers (Praxis:
 *    Europe/Berlin), nicht UTC. Ein Termin um 01:00 Uhr gehört zu dem Tag, an
 *    dem der Nutzer ihn erlebt hat.
 * 3. Eine Wiederaufnahme (`openReason === 'resume'`) ist KEINE Öffnung. Kehrt
 *    die App aus dem Hintergrund zurück, schreibt sie ein neues
 *    `project_open`; auf iOS/Android feuert das bei jedem App-Wechsel. Die
 *    Sitzung ist echt und ihre Zeit wurde wirklich verbracht — sie zählt voll
 *    in `totalTrackedMs`, `perDay.trackedMs`, `projectSessions` und die
 *    Rangliste. Als Öffnung gezählt wird sie nicht: sonst zeigten "Projekte
 *    geöffnet", Stundenhistogramm und Heatmap App-Wechsel statt Besuche.
 * 4. Eine Sitzung gehört GANZ zu dem Zeitpunkt, an dem sie GEÖFFNET wurde —
 *    zum Öffnungstag und, eine Ebene darüber, zum Zeitraum, der den
 *    Öffnungszeitpunkt enthält (`ActivityWindow`, siehe `placeSession`). Das
 *    ist dieselbe Regel auf zwei Skalen, und sie ersetzt jedes Zuschneiden an
 *    der Zeitraumgrenze: eine Sitzung, die über `toIso` hinausläuft, zählt mit
 *    ihrer VOLLEN gemessenen Dauer, sie wird nicht gekappt. Eine Sitzung, die
 *    vor `fromIso` begann, zählt hier gar nicht — sie gehört dem Zeitraum
 *    davor. Der Grund ist nicht Bequemlichkeit, sondern Zerlegbarkeit: nur so
 *    zählt jede Sitzung über aneinandergrenzende Zeiträume hinweg genau
 *    einmal, und nur so hängt der ausgewiesene Wert EINES Tages nicht davon
 *    ab, welches Fenster gerade gewählt ist. Zuschneiden würde denselben Tag
 *    unter "7 Tage" anders beziffern als unter "30 Tage".
 */

import type {
    ProjectSession,
    RouteDay,
    RoutePoint,
    UserActivityDay,
    UserActivityEvent,
    UserActivitySummary,
} from './userActivityTypes';

/** Beschriftung der Heatmap-Zeilen. Index 0 = Montag, wie im Datenvertrag. */
export const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const MS_PER_MINUTE = 60_000;

/** Platzhalter für "keine Angabe" — überall derselbe, damit die UI nicht rät. */
export const EMPTY_VALUE = '—';

const parseTime = (iso?: string | null): number => {
    if (!iso) return Number.NaN;
    return Date.parse(iso);
};

const isUsableTime = (iso?: string | null): boolean => Number.isFinite(parseTime(iso));

const pad2 = (value: number): string => `${value}`.padStart(2, '0');

/**
 * Chronologie je Ereignisstrom: aufsteigend nach Zeitpunkt, bei Gleichstand
 * stabil nach `id`. Ohne den zweiten Schlüssel entscheidet die Sortier-
 * implementierung über die Paarung, und dieselbe Eingabe liefert je nach
 * Laufzeit ein anderes Ergebnis.
 */
const byOccurredAt = (a: UserActivityEvent, b: UserActivityEvent): number => {
    const timeA = parseTime(a.occurredAt);
    const timeB = parseTime(b.occurredAt);
    if (timeA !== timeB) return timeA - timeB;
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
};

const byStringAsc = (a: string, b: string): number => (a === b ? 0 : a < b ? -1 : 1);

/** 'YYYY-MM-DD' in LOKALER Zeit. Leerer String, wenn der Zeitpunkt unbrauchbar ist. */
export const localDayKey = (iso?: string | null): string => {
    const date = new Date(parseTime(iso));
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/** Stunde 0–23 in LOKALER Zeit, -1 wenn der Zeitpunkt unbrauchbar ist. */
export const localHour = (iso?: string | null): number => {
    const date = new Date(parseTime(iso));
    if (Number.isNaN(date.getTime())) return -1;
    return date.getHours();
};

/** Wochentag als Index 0–6 mit Montag = 0 (JS zählt ab Sonntag). */
export const localWeekdayIndex = (iso?: string | null): number => {
    const date = new Date(parseTime(iso));
    if (Number.isNaN(date.getTime())) return -1;
    return (date.getDay() + 6) % DAYS_PER_WEEK;
};

/** 'YYYY-MM-DD' -> lokale Mitternacht. Über den lokalen Konstruktor, damit die
 *  Zeitumstellung nicht einen Tag verschiebt. */
export const parseDayKey = (day?: string | null): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** 'YYYY-MM-DD' -> 'dd.MM.yyyy'. */
export const formatDay = (day?: string | null): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');
    if (!match) return EMPTY_VALUE;
    return `${match[3]}.${match[2]}.${match[1]}`;
};

/** ISO-Zeitpunkt -> 'HH:mm' in lokaler Zeit. */
export const formatClock = (iso?: string | null): string => {
    const date = new Date(parseTime(iso));
    if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/** ISO-Zeitpunkt -> 'dd.MM.yyyy HH:mm' in lokaler Zeit. */
export const formatDayTime = (iso?: string | null): string => {
    if (!isUsableTime(iso)) return EMPTY_VALUE;
    return `${formatDay(localDayKey(iso))} ${formatClock(iso)}`;
};

/**
 * Dauer als "2 h 15 min" / "45 min". `null` (offene oder unbrauchbare Sitzung)
 * wird als Gedankenstrich ausgewiesen und NIE als "0 min" — sonst liest sich
 * eine fehlende Messung wie eine gemessene Null.
 */
export const formatDuration = (ms: number | null | undefined): string => {
    if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return EMPTY_VALUE;
    const totalMinutes = Math.floor(ms / MS_PER_MINUTE);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours} h ${minutes} min`;
    if (hours > 0) return `${hours} h`;
    return `${minutes} min`;
};

/** Ergebnis der Paarung: die Sitzungen und die Zahl der verworfenen Schliessen. */
export interface ProjectSessionPairing {
    sessions: ProjectSession[];
    /** `project_close`-Ereignisse ohne offene Sitzung. Am abgeschnittenen alten
     *  Rand des Zeitraums ist das systematisch und muss beziffert werden, statt
     *  stillschweigend zu verschwinden. */
    discardedCloseCount: number;
}

/**
 * Wiederaufnahme statt Öffnung? `null` gilt als 'normal' — die Spalte kam
 * später dazu, Ereignisse aus der Zeit davor sind gewöhnliche Öffnungen.
 */
const isResumeOpen = (event: UserActivityEvent): boolean =>
    event.eventType === 'project_open' && event.openReason === 'resume';

/**
 * Paart `project_open` und `project_close` zu Projektsitzungen.
 *
 * Vorgehen: je `sessionId` (eine Browser-Sitzung) chronologisch durchlaufen und
 * je `projectId` einen Stapel offener Sitzungen führen. Ein Schliessen nimmt
 * immer die JÜNGSTE offene Sitzung desselben Projekts vom Stapel (LIFO) —
 * verschachtelte Öffnungen werden so korrekt aufgelöst. Findet ein Schliessen
 * keine offene Sitzung, wird es verworfen; eine Sitzung ohne Anfang gibt es
 * nicht, und sie zu erfinden hiesse, den Anfangszeitpunkt zu raten. Verworfen
 * heisst aber nicht ungezählt: die Zahl wandert in `discardedCloseCount`.
 *
 * Eine Wiederaufnahme eröffnet eine echte neue Sitzung (keine Dauerschätzung
 * über die Hintergrundzeit hinweg) und wird über `resumed` markiert.
 */
/**
 * Paarung mit den verworfenen Schliessen-EREIGNISSEN statt nur ihrer Zahl.
 *
 * Nur intern: die Randerkennung (`boundaryCandidates`) braucht zu jedem
 * heimatlosen Schliessen die Sitzung und das Projekt, um genau dafür das
 * fehlende Öffnen nachladen zu können. Die öffentliche `pairProjectSessions`
 * bleibt bei der blossen Zahl.
 */
interface RawPairing {
    sessions: ProjectSession[];
    discarded: UserActivityEvent[];
}

const pairInternal = (events: UserActivityEvent[]): RawPairing => {
    const relevant = (events || []).filter(event =>
        !!event
        && (event.eventType === 'project_open' || event.eventType === 'project_close')
        && !!event.projectId
        && isUsableTime(event.occurredAt));

    // Getrennt nach Browser-Sitzung: zwei parallel offene Tabs dürfen sich nicht
    // gegenseitig Sitzungen schliessen.
    const bySession = new Map<string, UserActivityEvent[]>();
    for (const event of relevant) {
        const key = event.sessionId || '';
        const bucket = bySession.get(key);
        if (bucket) bucket.push(event);
        else bySession.set(key, [event]);
    }

    const sessions: ProjectSession[] = [];
    const discarded: UserActivityEvent[] = [];
    for (const [sessionId, bucket] of bySession) {
        const ordered = [...bucket].sort(byOccurredAt);
        const openStacks = new Map<string, ProjectSession[]>();

        for (const event of ordered) {
            const projectId = event.projectId as string;

            if (event.eventType === 'project_open') {
                const session: ProjectSession = {
                    sessionId,
                    projectId,
                    projectNumber: event.projectNumber ?? null,
                    projectName: event.projectName ?? null,
                    openedAt: event.occurredAt,
                    closedAt: null,
                    closeReason: null,
                    durationMs: null,
                    resumed: isResumeOpen(event),
                };
                sessions.push(session);
                const stack = openStacks.get(projectId);
                if (stack) stack.push(session);
                else openStacks.set(projectId, [session]);
                continue;
            }

            const stack = openStacks.get(projectId);
            const open = stack && stack.length > 0 ? stack.pop() : undefined;
            // Schliessen ohne offene Sitzung -> verwerfen, keine Geistersitzung.
            // Verworfen, aber gezählt: siehe `discardedCloseCount`.
            if (!open) {
                discarded.push(event);
                continue;
            }

            open.closedAt = event.occurredAt;
            open.closeReason = event.closeReason ?? null;
            const duration = parseTime(event.occurredAt) - parseTime(open.openedAt);
            // Negative Dauer heisst falsch gestellte Uhr, nicht "0 Minuten".
            open.durationMs = duration >= 0 ? duration : null;
        }
    }

    sessions.sort((a, b) => {
        const timeA = parseTime(a.openedAt);
        const timeB = parseTime(b.openedAt);
        if (timeA !== timeB) return timeA - timeB;
        if (a.sessionId !== b.sessionId) return byStringAsc(a.sessionId, b.sessionId);
        return byStringAsc(a.projectId, b.projectId);
    });

    return { sessions, discarded };
};

export const pairProjectSessions = (events: UserActivityEvent[]): ProjectSessionPairing => {
    const { sessions, discarded } = pairInternal(events);
    return { sessions, discardedCloseCount: discarded.length };
};

/** Nur die Sitzungen der Paarung — für Aufrufer, die die verworfenen
 *  Schliessen nicht brauchen. */
export const buildProjectSessions = (events: UserActivityEvent[]): ProjectSession[] =>
    pairProjectSessions(events).sessions;

// ─── Ränder des Zeitraums ───────────────────────────────────────────────────
//
// Die Ereignisabfrage begrenzt serverseitig auf [fromIso, toIso]. Zwei
// Sitzungsarten liegen damit nur zur HÄLFTE in der geladenen Reihe:
//
//   (a) vor `fromIso` geöffnet, INNERHALB geschlossen — das Öffnen fehlt, das
//       Schliessen wird heimatlos verworfen und erscheint als Datenfehler,
//       obwohl es keiner ist.
//   (b) INNERHALB geöffnet, NACH `toIso` geschlossen — das Schliessen fehlt,
//       die Sitzung steht auf Dauer als "offen", obwohl sie geschlossen wurde.
//
// Beides wird nicht geschätzt und nicht weggerechnet, sondern NACHGESEHEN: die
// Ladeschicht holt zu genau diesen Rändern die fehlenden Gegenstücke (siehe
// hooks/useUserActivity.ts). Die beiden Funktionen hier sagen ihr, wonach sie
// fragen muss, und welche der nachgeladenen Zeilen sie einspeisen darf.

/** Ein Paar aus Browser-Sitzung und Projekt — der Schlüssel der Paarung. */
export interface SessionProjectRef {
    sessionId: string;
    projectId: string;
    /** Wie viele Gegenstücke diesem Paar am Rand fehlen. */
    count: number;
}

export interface BoundaryCandidates {
    /** Verworfene Schliessen — ihr Öffnen könnte VOR dem Zeitraum liegen. */
    unmatchedCloses: SessionProjectRef[];
    /** Am Zeitraumende offene Sitzungen — ihr Schliessen könnte DANACH liegen. */
    unclosedOpens: SessionProjectRef[];
}

const refKey = (sessionId: string, projectId: string): string => `${sessionId}\u0000${projectId}`;

const tally = (into: Map<string, SessionProjectRef>, sessionId: string, projectId: string): void => {
    const key = refKey(sessionId, projectId);
    const found = into.get(key);
    if (found) found.count += 1;
    else into.set(key, { sessionId, projectId, count: 1 });
};

/**
 * Welche Ränder des geladenen Zeitraums ein Gegenstück ausserhalb haben könnten.
 *
 * `count` ist keine Schätzung, sondern die exakte Zahl der fehlenden
 * Gegenstücke: so viele Schliessen blieben heimatlos, so viele Sitzungen blieben
 * offen. Mehr darf die Ladeschicht nicht einspeisen — jedes Ereignis darüber
 * hinaus gehört zu einer Sitzung, die vollständig ausserhalb liegt.
 */
export const boundaryCandidates = (events: UserActivityEvent[]): BoundaryCandidates => {
    const { sessions, discarded } = pairInternal(events);

    const closes = new Map<string, SessionProjectRef>();
    for (const event of discarded) {
        if (!event.projectId) continue;
        tally(closes, event.sessionId || '', event.projectId);
    }

    const opens = new Map<string, SessionProjectRef>();
    for (const session of sessions) {
        if (session.closedAt !== null) continue;
        tally(opens, session.sessionId || '', session.projectId);
    }

    return { unmatchedCloses: [...closes.values()], unclosedOpens: [...opens.values()] };
};

/** Ergebnis einer Randauflösung. */
export interface BoundaryResolution {
    /** Die Ereignisse, die in den Fensterstrom aufgenommen werden dürfen. */
    events: UserActivityEvent[];
    /** Gegenstücke, für die in der übergebenen Vorgeschichte keines zu finden war. */
    unresolved: number;
}

const needMap = (candidates: SessionProjectRef[]): Map<string, number> => {
    const need = new Map<string, number>();
    for (const candidate of candidates || []) {
        if (!candidate || !candidate.projectId) continue;
        const key = refKey(candidate.sessionId || '', candidate.projectId);
        need.set(key, (need.get(key) || 0) + Math.max(1, candidate.count || 1));
    }
    return need;
};

const countUnresolved = (need: Map<string, number>, found: Map<string, number>): number => {
    let open = 0;
    for (const [key, wanted] of need) open += Math.max(0, wanted - (found.get(key) || 0));
    return open;
};

/**
 * Fall (a): die Öffnungen heraussuchen, die am Zeitraumanfang noch offen waren.
 *
 * Gelesen wird RÜCKWÄRTS vom Zeitraumanfang. Jedes Schliessen erzeugt eine
 * Schuld, jede Öffnung tilgt eine — eine Öffnung ohne Schuld ist genau die, die
 * beim Erreichen der Zeitraumgrenze noch offen war. Zurückgegeben werden NUR
 * diese Öffnungen, nicht die halbe Vorgeschichte: die vollständig vor dem
 * Zeitraum liegenden Sitzungen gehen die Auswertung nichts an, und ein
 * abgeschnittener Vorlauf würde aus ihren Schliessen sonst neue Scheinfehler
 * machen.
 */
export const precedingOpenEvents = (
    candidates: SessionProjectRef[],
    events: UserActivityEvent[],
): BoundaryResolution => {
    const need = needMap(candidates);
    const debt = new Map<string, number>();
    const found = new Map<string, number>();
    const picked: UserActivityEvent[] = [];

    // Defensiv selbst sortieren: die Funktion muss unabhängig davon stimmen, in
    // welcher Reihenfolge der Aufrufer die Seiten zusammengelegt hat.
    const descending = [...(events || [])].sort(byOccurredAt).reverse();

    for (const event of descending) {
        if (!event || !event.projectId || !isUsableTime(event.occurredAt)) continue;
        const key = refKey(event.sessionId || '', event.projectId);
        const wanted = need.get(key);
        if (wanted === undefined || (found.get(key) || 0) >= wanted) continue;

        if (event.eventType === 'project_close') {
            debt.set(key, (debt.get(key) || 0) + 1);
            continue;
        }
        if (event.eventType !== 'project_open') continue;

        const owed = debt.get(key) || 0;
        if (owed > 0) {
            debt.set(key, owed - 1);
            continue;
        }
        picked.push(event);
        found.set(key, (found.get(key) || 0) + 1);
    }

    return { events: picked, unresolved: countUnresolved(need, found) };
};

/**
 * Fall (b): die Schliessen heraussuchen, die zu am Zeitraumende offenen
 * Sitzungen gehören.
 *
 * Spiegelbild von `precedingOpenEvents`: vorwärts gelesen erzeugt jede Öffnung
 * NACH dem Zeitraum eine Schuld, jedes Schliessen tilgt eine — ein Schliessen
 * ohne Schuld gehört zu einer Sitzung, die schon vor der Zeitraumgrenze offen
 * war. Ohne diese Verrechnung bekäme eine Sitzung das Schliessen einer erst
 * danach geöffneten und damit eine zu KURZE Dauer.
 */
export const followingCloseEvents = (
    candidates: SessionProjectRef[],
    events: UserActivityEvent[],
): BoundaryResolution => {
    const need = needMap(candidates);
    const debt = new Map<string, number>();
    const found = new Map<string, number>();
    const picked: UserActivityEvent[] = [];

    const ascending = [...(events || [])].sort(byOccurredAt);

    for (const event of ascending) {
        if (!event || !event.projectId || !isUsableTime(event.occurredAt)) continue;
        const key = refKey(event.sessionId || '', event.projectId);
        const wanted = need.get(key);
        if (wanted === undefined || (found.get(key) || 0) >= wanted) continue;

        if (event.eventType === 'project_open') {
            debt.set(key, (debt.get(key) || 0) + 1);
            continue;
        }
        if (event.eventType !== 'project_close') continue;

        const owed = debt.get(key) || 0;
        if (owed > 0) {
            debt.set(key, owed - 1);
            continue;
        }
        picked.push(event);
        found.set(key, (found.get(key) || 0) + 1);
    }

    return { events: picked, unresolved: countUnresolved(need, found) };
};

// ─── Zeitraum ───────────────────────────────────────────────────────────────

/** Der ausgewertete Zeitraum. `null` = an dieser Seite offen. */
export interface ActivityWindow {
    fromIso: string | null;
    toIso: string | null;
}

type SessionPlacement = 'inside' | 'preceding' | 'outside';

/**
 * Gehört die Sitzung in den Zeitraum? Entschieden wird ausschliesslich am
 * ÖFFNUNGSZEITPUNKT (Regel 4 im Kopf dieser Datei).
 *
 * 'inside'    — im Zeitraum geöffnet. Zählt mit ihrer vollen gemessenen Dauer,
 *               auch wenn sie über `toIso` hinausläuft. Nichts wird gekappt.
 * 'preceding' — vor dem Zeitraum geöffnet und in ihn hineinreichend. Zählt
 *               NICHT: ihre Zeit gehört dem Zeitraum davor, in dem sie auch
 *               ausgewiesen wird. Sie wird aber beziffert, damit ihr Schliessen
 *               nicht als Datenfehler gelesen wird.
 * 'outside'   — vollständig ausserhalb. Geht die Auswertung nichts an.
 */
const placeSession = (session: ProjectSession, timeWindow?: ActivityWindow | null): SessionPlacement => {
    if (!timeWindow) return 'inside';

    const opened = parseTime(session.openedAt);
    if (!Number.isFinite(opened)) return 'outside';

    const to = timeWindow.toIso ? parseTime(timeWindow.toIso) : Number.NaN;
    if (Number.isFinite(to) && opened > to) return 'outside';

    const from = timeWindow.fromIso ? parseTime(timeWindow.fromIso) : Number.NaN;
    if (!Number.isFinite(from) || opened >= from) return 'inside';

    // Vor dem Zeitraum geöffnet: nur wer in ihn hineinreicht, wird beziffert.
    const closed = session.closedAt ? parseTime(session.closedAt) : Number.NaN;
    if (!Number.isFinite(closed)) return 'preceding';
    return closed >= from ? 'preceding' : 'outside';
};

/** Liegt ein einzelner Zeitpunkt (z. B. ein `app_start`) im Zeitraum? */
const timeInWindow = (iso: string, timeWindow?: ActivityWindow | null): boolean => {
    if (!timeWindow) return true;
    const at = parseTime(iso);
    if (!Number.isFinite(at)) return false;
    const from = timeWindow.fromIso ? parseTime(timeWindow.fromIso) : Number.NaN;
    if (Number.isFinite(from) && at < from) return false;
    const to = timeWindow.toIso ? parseTime(timeWindow.toIso) : Number.NaN;
    return !(Number.isFinite(to) && at > to);
};

const emptyDay = (day: string): UserActivityDay => ({
    day,
    startCount: 0,
    projectOpenCount: 0,
    trackedMs: 0,
});

const zeros = (length: number): number[] => {
    const row: number[] = [];
    for (let index = 0; index < length; index += 1) row.push(0);
    return row;
};

/**
 * Kennzahlen eines Nutzers über den übergebenen Ereignisstrom.
 *
 * `timeWindow` ist der ausgewertete Zeitraum. Er darf MEHR Ereignisse enthalten als
 * den Zeitraum selbst — genau dafür ist er da: die Ladeschicht speist die
 * Randereignisse mit ein (das fehlende Öffnen von vor `fromIso`, das fehlende
 * Schliessen von nach `toIso`), damit die Paarung stimmt, und `timeWindow`
 * entscheidet anschliessend, welche SITZUNGEN in die Zahlen gehören. Ohne
 * `timeWindow` zählt alles, was im Strom steht — das ist der Fall der reinen
 * Rechenaufrufe und der Tests.
 */
export const summarizeUserActivity = (
    events: UserActivityEvent[],
    userId: string,
    timeWindow?: ActivityWindow | null,
): UserActivitySummary => {
    const mine = (events || []).filter(event =>
        !!event && event.userId === userId && isUsableTime(event.occurredAt));

    const starts = mine
        .filter(event => event.eventType === 'app_start' && timeInWindow(event.occurredAt, timeWindow))
        .sort(byOccurredAt);
    const { sessions: allSessions, discarded } = pairInternal(mine);
    const discardedCloseCount = discarded.length;

    // Zuordnung über den Öffnungszeitpunkt (Regel 4). Was vor dem Zeitraum
    // begann, wird gezählt, aber nicht verrechnet; was vollständig davor liegt,
    // taucht nirgends auf.
    const projectSessions: ProjectSession[] = [];
    let precedingSessionCount = 0;
    for (const session of allSessions) {
        const placement = placeSession(session, timeWindow);
        if (placement === 'inside') projectSessions.push(session);
        else if (placement === 'preceding') precedingSessionCount += 1;
    }

    const perDayMap = new Map<string, UserActivityDay>();
    const dayEntry = (day: string): UserActivityDay => {
        const existing = perDayMap.get(day);
        if (existing) return existing;
        const created = emptyDay(day);
        perDayMap.set(day, created);
        return created;
    };

    for (const start of starts) {
        dayEntry(localDayKey(start.occurredAt)).startCount += 1;
    }

    const hourHistogram = zeros(HOURS_PER_DAY);
    const weekdayHourHeatmap: number[][] = [];
    for (let weekday = 0; weekday < DAYS_PER_WEEK; weekday += 1) {
        weekdayHourHeatmap.push(zeros(HOURS_PER_DAY));
    }

    let openSessionCount = 0;
    let totalTrackedMs = 0;
    let resumeCount = 0;

    for (const session of projectSessions) {
        // Eine Sitzung gehört ganz zu dem Tag, an dem sie geöffnet wurde. Über
        // Mitternacht wird nicht gesplittet und schon gar nicht doppelt gezählt.
        const day = dayEntry(localDayKey(session.openedAt));

        if (session.resumed) {
            // Rückkehr aus dem Hintergrund: keine Öffnung, also weder in der
            // Tages- noch in der Stunden- noch in der Wochenzählung. Der Tag
            // bleibt ein aktiver Tag, und die Zeit unten zählt voll mit.
            resumeCount += 1;
        } else {
            day.projectOpenCount += 1;

            const hour = localHour(session.openedAt);
            const weekday = localWeekdayIndex(session.openedAt);
            if (hour >= 0) hourHistogram[hour] += 1;
            if (hour >= 0 && weekday >= 0) weekdayHourHeatmap[weekday][hour] += 1;
        }

        // "Offen" heisst hier: traegt nichts zur erfassten Zeit bei. Das ist
        // nicht dasselbe wie "kein Close-Event" — eine Sitzung mit negativer
        // Dauer (Uhrfehler) hat ein closedAt, aber durationMs=null, und muss
        // deshalb genauso als offen gelten wie eine Sitzung ohne jedes Close.
        // Sonst faellt sie aus JEDER Kennzahl heraus, ohne irgendwo aufzutauchen.
        if (session.durationMs === null) openSessionCount += 1;
        if (session.durationMs !== null) {
            totalTrackedMs += session.durationMs;
            day.trackedMs += session.durationMs;
        }
    }

    const perDay = [...perDayMap.values()].sort((a, b) => byStringAsc(a.day, b.day));

    return {
        userId,
        firstStartAt: starts.length > 0 ? starts[0].occurredAt : null,
        lastStartAt: starts.length > 0 ? starts[starts.length - 1].occurredAt : null,
        startCount: starts.length,
        // Ohne Wiederaufnahmen: gezählt werden Besuche, nicht App-Wechsel.
        projectOpenCount: projectSessions.length - resumeCount,
        openSessionCount,
        totalTrackedMs,
        activeDays: perDay.length,
        resumeCount,
        discardedCloseCount,
        precedingSessionCount,
        projectSessions,
        perDay,
        hourHistogram,
        weekdayHourHeatmap,
    };
};

/** Eine Zusammenfassung je Nutzer im Strom, aufsteigend nach `userId`. */
export const summarizeAllUsers = (
    events: UserActivityEvent[],
    timeWindow?: ActivityWindow | null,
): UserActivitySummary[] => {
    const userIds: string[] = [];
    const seen = new Set<string>();
    for (const event of events || []) {
        if (!event || !event.userId || seen.has(event.userId)) continue;
        seen.add(event.userId);
        userIds.push(event.userId);
    }
    return userIds
        .sort(byStringAsc)
        .map(userId => summarizeUserActivity(events, userId, timeWindow));
};

/**
 * Tagesrouten: die an einem Tag geöffneten Projekte in zeitlicher Reihenfolge.
 *
 * Die Koordinaten stammen aus den Projekt-/Kundenadressen, NICHT vom Gerät —
 * das ist kein Bewegungsprofil. Direkt aufeinanderfolgende Öffnungen desselben
 * Projekts (Hin- und Herspringen im Programm) ergeben EINEN Punkt; ein späterer
 * Rücksprung nach zwischenzeitlich anderem Projekt ist dagegen eine echte
 * zweite Station und bekommt einen eigenen Punkt. Projekte ohne Koordinaten
 * werden ausgewiesen, nicht stillschweigend geschluckt.
 */
export const buildRouteDays = (
    events: UserActivityEvent[],
    coordinates: Map<string, { lat: number; lng: number }>,
): RouteDay[] => {
    const opens = (events || [])
        .filter(event =>
            !!event
            && event.eventType === 'project_open'
            && !!event.projectId
            && isUsableTime(event.occurredAt))
        .sort(byOccurredAt);

    const byDay = new Map<string, UserActivityEvent[]>();
    for (const event of opens) {
        const day = localDayKey(event.occurredAt);
        const bucket = byDay.get(day);
        if (bucket) bucket.push(event);
        else byDay.set(day, [event]);
    }

    const days: RouteDay[] = [];
    for (const [day, bucket] of byDay) {
        const collapsed: UserActivityEvent[] = [];
        for (const event of bucket) {
            const previous = collapsed[collapsed.length - 1];
            if (previous && previous.projectId === event.projectId) continue;
            collapsed.push(event);
        }

        const points: RoutePoint[] = [];
        const missingCoordinates: Array<{ projectId: string; projectNumber: string | null }> = [];
        const missingSeen = new Set<string>();

        for (const event of collapsed) {
            const projectId = event.projectId as string;
            const coordinate = coordinates ? coordinates.get(projectId) : undefined;
            const hasCoordinate = !!coordinate
                && Number.isFinite(coordinate.lat)
                && Number.isFinite(coordinate.lng);

            if (!hasCoordinate) {
                if (!missingSeen.has(projectId)) {
                    missingSeen.add(projectId);
                    missingCoordinates.push({
                        projectId,
                        projectNumber: event.projectNumber ?? null,
                    });
                }
                continue;
            }

            points.push({
                // Fortlaufend über die tatsächlich gezeichneten Punkte, damit die
                // Nummern auf der Karte keine Lücken haben.
                order: points.length + 1,
                projectId,
                projectNumber: event.projectNumber ?? null,
                projectName: event.projectName ?? null,
                lat: (coordinate as { lat: number; lng: number }).lat,
                lng: (coordinate as { lat: number; lng: number }).lng,
                occurredAt: event.occurredAt,
            });
        }

        days.push({ day, points, missingCoordinates });
    }

    return days.sort((a, b) => byStringAsc(a.day, b.day));
};

export interface ProjectDurationRank {
    projectId: string;
    projectNumber: string | null;
    projectName: string | null;
    totalMs: number;
    /** Alle Sitzungen des Projekts, offene wie abgeschlossene. */
    sessionCount: number;
    /** Teilmenge von `sessionCount` ohne Zeitbeitrag — dieselbe Regel wie
     *  `UserActivitySummary.openSessionCount`: `durationMs === null`. */
    openSessionCount: number;
}

/**
 * Rangliste der Projekte nach erfasster Verweildauer (nur abgeschlossene
 * Sitzungen). Offene Sitzungen werden mitgezählt, aber nicht in Zeit umgerechnet
 * — sie stehen als eigene Zahl daneben.
 */
export const topProjectsByDuration = (
    sessions: ProjectSession[],
    limit?: number,
): ProjectDurationRank[] => {
    const byProject = new Map<string, ProjectDurationRank>();

    for (const session of sessions || []) {
        if (!session || !session.projectId) continue;
        let entry = byProject.get(session.projectId);
        if (!entry) {
            entry = {
                projectId: session.projectId,
                projectNumber: session.projectNumber ?? null,
                projectName: session.projectName ?? null,
                totalMs: 0,
                sessionCount: 0,
                openSessionCount: 0,
            };
            byProject.set(session.projectId, entry);
        }
        // Anzeigename aus der jüngsten Beschriftung, falls die erste leer war.
        if (!entry.projectNumber && session.projectNumber) entry.projectNumber = session.projectNumber;
        if (!entry.projectName && session.projectName) entry.projectName = session.projectName;

        entry.sessionCount += 1;
        // Dieselbe Regel wie in summarizeUserActivity: "offen" = ohne
        // Zeitbeitrag (durationMs===null), nicht "ohne closedAt".
        if (session.durationMs === null) entry.openSessionCount += 1;
        if (session.durationMs !== null) entry.totalMs += session.durationMs;
    }

    const ranked = [...byProject.values()].sort((a, b) => {
        if (a.totalMs !== b.totalMs) return b.totalMs - a.totalMs;
        if (a.sessionCount !== b.sessionCount) return b.sessionCount - a.sessionCount;
        return byStringAsc(a.projectId, b.projectId);
    });

    if (limit === undefined || limit === null || limit < 0) return ranked;
    return ranked.slice(0, limit);
};

/** Alle Tagesschlüssel von `fromDay` bis `toDay` einschliesslich, aufsteigend. */
export const dayRange = (fromDay: string, toDay: string): string[] => {
    const start = parseDayKey(fromDay);
    const end = parseDayKey(toDay);
    if (!start || !end || start.getTime() > end.getTime()) return [];

    const keys: string[] = [];
    const cursor = new Date(start.getTime());
    // Schrittweite über den lokalen Kalender, nicht über +24h: an den
    // Umstellungstagen hat ein Tag 23 bzw. 25 Stunden.
    while (cursor.getTime() <= end.getTime()) {
        keys.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`);
        cursor.setDate(cursor.getDate() + 1);
        cursor.setHours(0, 0, 0, 0);
    }
    return keys;
};

/**
 * Tagesreihe ohne Lücken: jeder Tag des Zeitraums kommt vor, fehlende Tage mit
 * Nullen. Ein Balkendiagramm, das leere Tage überspringt, staucht die Zeitachse
 * und behauptet damit eine Aktivität, die es nicht gab.
 */
export const fillDayRange = (
    perDay: UserActivityDay[],
    fromDay: string,
    toDay: string,
): UserActivityDay[] => {
    const byDay = new Map<string, UserActivityDay>();
    for (const entry of perDay || []) {
        if (entry && entry.day) byDay.set(entry.day, entry);
    }
    return dayRange(fromDay, toDay).map(day => byDay.get(day) || emptyDay(day));
};

/** Durchschnittliche Verweildauer je abgeschlossener Sitzung, `null` ohne Messwerte. */
export const averageSessionMs = (sessions: ProjectSession[]): number | null => {
    let total = 0;
    let count = 0;
    for (const session of sessions || []) {
        if (!session || session.durationMs === null) continue;
        total += session.durationMs;
        count += 1;
    }
    return count > 0 ? Math.round(total / count) : null;
};

/** Grösster Wert der Heatmap — für die Farbskala der UI. */
export const heatmapPeak = (heatmap: number[][]): number => {
    let peak = 0;
    for (const row of heatmap || []) {
        for (const value of row || []) {
            if (value > peak) peak = value;
        }
    }
    return peak;
};
